import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';
import './VoiceMentor.css';

function VoiceMentor({ lessonContent, lessonTitle, learningPath, onClose }) {
    const [isListening, setIsListening] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [messages, setMessages] = useState([]);
    const [error, setError] = useState(null);
    const [voiceSupported, setVoiceSupported] = useState(true);
    const [selectedVoice, setSelectedVoice] = useState(null);
    const [availableVoices, setAvailableVoices] = useState([]);
    const [showSettings, setShowSettings] = useState(false);
    const [speechRate, setSpeechRate] = useState(1.0);
    const [wakeWordEnabled, setWakeWordEnabled] = useState(true);
    const [isWakeListening, setIsWakeListening] = useState(false);
    const [lectureMode, setLectureMode] = useState(false);
    const [lecturePosition, setLecturePosition] = useState(0);
    const [lecturePaused, setLecturePaused] = useState(false);
    const [lectureChunks, setLectureChunks] = useState([]);
    const [showLessonPicker, setShowLessonPicker] = useState(true); // Show lesson picker first
    const [selectedLesson, setSelectedLesson] = useState(null);
    const [currentLessonContent, setCurrentLessonContent] = useState(lessonContent || '');
    const [currentLessonTitle, setCurrentLessonTitle] = useState(lessonTitle || '');
    const [lectureComplete, setLectureComplete] = useState(false);
    const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
    const [allLessons, setAllLessons] = useState([]);

    const recognitionRef = useRef(null);
    const wakeRecognitionRef = useRef(null);
    const synthRef = useRef(window.speechSynthesis);
    const messagesEndRef = useRef(null);
    const conversationHistoryRef = useRef([]);
    const silenceTimeoutRef = useRef(null);
    const lastSpeechTimeRef = useRef(null);
    const gracePeriodRef = useRef(false);
    const shouldBeListeningRef = useRef(false); // Track intended listening state
    const manualStopRef = useRef(false); // Track if stop was intentional
    const goToNextLessonRef = useRef(null); // Ref for next lesson function
    const transcriptRef = useRef(''); // Track transcript in real-time to avoid stale closure issues

    // Wake word phrases that activate listening
    const WAKE_PHRASES = ['learn viva', 'learnviva', 'hey viva', 'hey learnviva', 'ok viva', 'okay viva'];

    // Close phrases that end the mentor session
    const CLOSE_PHRASES = ['goodbye viva', 'bye viva', 'close mentor', 'exit mentor'];

    // Continue phrases that resume lecture
    const CONTINUE_PHRASES = ['please continue', 'continue please', 'okay continue', 'ok continue', 'continue learning', 'resume lecture'];

    // Next lesson phrases
    const NEXT_LESSON_PHRASES = ['next lesson', 'continue to next', 'next class', 'go to next', 'start next lesson'];

    // Silence timeout duration (3 seconds of silence after speaking)
    const SILENCE_TIMEOUT = 3000;

    // Grace period before silence detection starts (give user time to start speaking)
    const GRACE_PERIOD = 5000;

    // Initialize speech recognition with continuous listening and silence detection
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
            setVoiceSupported(false);
            setError('Speech recognition not supported in this browser. Try Chrome or Edge.');
            return;
        }

        // Main recognition - continuous with silence detection
        const recognition = new SpeechRecognition();
        recognition.continuous = true; // Keep listening
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onstart = () => {
            console.log('🎤 Recognition STARTED');
            shouldBeListeningRef.current = true;
            manualStopRef.current = false;
            setIsListening(true);
            setError(null);
            // Start with grace period - don't start silence detection immediately
            gracePeriodRef.current = true;
            lastSpeechTimeRef.current = Date.now();
            // Wait for grace period before starting silence detection
            setTimeout(() => {
                gracePeriodRef.current = false;
                // Only start if still listening
                if (shouldBeListeningRef.current && recognitionRef.current) {
                    startSilenceDetection();
                }
            }, GRACE_PERIOD);
        };

        recognition.onresult = (event) => {
            let finalTranscript = '';
            let interimTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcriptText = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalTranscript += transcriptText;
                } else {
                    interimTranscript += transcriptText;
                }
            }

            // User is speaking! Update last speech time
            lastSpeechTimeRef.current = Date.now();

            // If we were in grace period, end it now and start silence detection
            // since the user has started speaking
            if (gracePeriodRef.current && (finalTranscript || interimTranscript)) {
                gracePeriodRef.current = false;
                startSilenceDetection();
            }

            const currentText = finalTranscript || interimTranscript;
            setTranscript(currentText);
            transcriptRef.current = currentText; // Keep ref in sync for real-time access

            // If we have a final transcript, check for commands
            if (finalTranscript) {
                // Check for close phrases
                const lowerTranscript = finalTranscript.toLowerCase().trim();
                const closeDetected = CLOSE_PHRASES.some(phrase => lowerTranscript.includes(phrase));

                if (closeDetected) {
                    stopListeningAndProcess();
                    synthRef.current.cancel();
                    const goodbye = new SpeechSynthesisUtterance("Happy studying! See you next time.");
                    synthRef.current.speak(goodbye);
                    setTimeout(() => onClose(), 1500);
                    return;
                }

                // Check for continue phrases (resume lecture)
                const continueDetected = CONTINUE_PHRASES.some(phrase => lowerTranscript.includes(phrase));
                if (continueDetected && lectureMode && lecturePaused) {
                    stopListeningAndProcess();
                    setLecturePaused(false);
                    // Resume lecture from where we left off
                    continueLecture();
                    return;
                }

                // Check for next lesson phrases (after lecture is complete)
                const nextLessonDetected = NEXT_LESSON_PHRASES.some(phrase => lowerTranscript.includes(phrase));
                if (nextLessonDetected && goToNextLessonRef.current) {
                    stopListeningAndProcess();
                    goToNextLessonRef.current();
                    return;
                }

                // Reset silence timer - user is still speaking
                resetSilenceTimer();
            }
        };

        recognition.onerror = (event) => {
            console.error('Speech recognition error:', event.error);
            if (event.error === 'not-allowed') {
                setError('Microphone access denied. Please allow microphone access.');
                shouldBeListeningRef.current = false;
                manualStopRef.current = true;
                setIsListening(false);
            } else if (event.error === 'aborted') {
                // Aborted is normal during restart, don't treat as error
                console.log('Recognition aborted (normal during restart)');
            } else if (event.error === 'no-speech') {
                // No speech detected - this is normal, recognition will auto-restart in onend
                console.log('No speech detected yet, waiting...');
            } else {
                setError(`Speech error: ${event.error}`);
            }
        };

        recognition.onend = () => {
            console.log('🎤 Recognition ENDED - shouldBe:', shouldBeListeningRef.current, 'manual:', manualStopRef.current);
            clearSilenceTimer();

            // If we should still be listening and didn't manually stop, restart recognition
            if (shouldBeListeningRef.current && !manualStopRef.current) {
                console.log('🔄 Auto-restarting recognition...');
                // Browser auto-stopped - restart after short delay
                setTimeout(() => {
                    if (shouldBeListeningRef.current && recognitionRef.current) {
                        try {
                            recognitionRef.current.start();
                            console.log('🎤 Recognition RESTARTED');
                        } catch (err) {
                            console.error('Error restarting recognition:', err);
                            // If restart fails, update state
                            shouldBeListeningRef.current = false;
                            setIsListening(false);
                        }
                    }
                }, 100);
            } else {
                // We intentionally stopped
                setIsListening(false);
            }
        };

        recognitionRef.current = recognition;

        // Wake word recognition (background listening during lecture)
        const wakeRecognition = new SpeechRecognition();
        wakeRecognition.continuous = true;
        wakeRecognition.interimResults = true;
        wakeRecognition.lang = 'en-US';

        wakeRecognition.onstart = () => {
            setIsWakeListening(true);
        };

        wakeRecognition.onresult = (event) => {
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcriptText = event.results[i][0].transcript.toLowerCase().trim();

                const wakeDetected = WAKE_PHRASES.some(phrase =>
                    transcriptText.includes(phrase)
                );

                if (wakeDetected && !shouldBeListeningRef.current && !isProcessing) {
                    console.log('Wake word detected:', transcriptText);
                    wakeRecognition.stop();

                    // If in lecture mode, pause the lecture
                    if (lectureMode && isSpeaking) {
                        synthRef.current.cancel();
                        setIsSpeaking(false);
                        setLecturePaused(true);
                    }

                    playActivationSound();

                    // Start listening for question
                    setTimeout(() => {
                        try {
                            shouldBeListeningRef.current = true;
                            manualStopRef.current = false;
                            recognitionRef.current?.start();
                        } catch (err) {
                            console.error('Error starting recognition after wake:', err);
                            shouldBeListeningRef.current = false;
                        }
                    }, 200);
                }
            }
        };

        wakeRecognition.onerror = (event) => {
            if (event.error !== 'aborted' && event.error !== 'no-speech') {
                console.log('Wake recognition error:', event.error);
            }
            setIsWakeListening(false);
        };

        wakeRecognition.onend = () => {
            setIsWakeListening(false);
            // Restart wake listening if in lecture mode or enabled (but not if main recognition is active)
            if ((wakeWordEnabled || lectureMode) && !shouldBeListeningRef.current && !isProcessing) {
                setTimeout(() => {
                    try {
                        wakeRecognition.start();
                    } catch (err) {
                        // Ignore
                    }
                }, 500);
            }
        };

        wakeRecognitionRef.current = wakeRecognition;

        // Load available voices
        const loadVoices = () => {
            const voices = synthRef.current.getVoices();
            // Deep/older male voice names to look for (in priority order)
            // These are common deep male voices across different platforms
            const deepMaleVoiceNames = [
                // Windows voices - deeper male options
                'Microsoft Mark', 'Microsoft David', 'Microsoft Guy', 'Microsoft Roger',
                // macOS/iOS voices - deeper male options
                'Daniel', 'Alex', 'Fred', 'Thomas', 'Oliver',
                // Google voices
                'Google UK English Male', 'Google US English',
                // Edge/Chrome voices
                'Microsoft Guy Online', 'Microsoft Roger Online',
                // General deep voice keywords
                'Male', 'Guy', 'Roger', 'Mark', 'David', 'Thomas', 'Oliver', 'Fred'
            ];

            // Find deep male voices
            const deepMaleVoices = voices.filter(v =>
                v.lang.startsWith('en') &&
                deepMaleVoiceNames.some(name => v.name.includes(name))
            );

            // Sort by priority - voices that appear earlier in our list are preferred
            deepMaleVoices.sort((a, b) => {
                const aIndex = deepMaleVoiceNames.findIndex(name => a.name.includes(name));
                const bIndex = deepMaleVoiceNames.findIndex(name => b.name.includes(name));
                return aIndex - bIndex;
            });

            // Fallback to any English voice
            const englishVoices = voices.filter(v => v.lang.startsWith('en'));

            // Store available deep voices for voice selector
            setAvailableVoices(deepMaleVoices.length > 0 ? deepMaleVoices : englishVoices);

            // Set default to the deepest voice we can find
            if (!selectedVoice) {
                setSelectedVoice(deepMaleVoices[0] || englishVoices[0] || voices[0]);
            }
        };

        loadVoices();
        synthRef.current.onvoiceschanged = loadVoices;

        return () => {
            shouldBeListeningRef.current = false;
            manualStopRef.current = true;
            clearSilenceTimer();
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
            if (wakeRecognitionRef.current) {
                wakeRecognitionRef.current.abort();
            }
            synthRef.current.cancel();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Extract lessons from learning path
    useEffect(() => {
        if (learningPath) {
            const lessons = [];

            if (learningPath.levels) {
                learningPath.levels.forEach((level, levelIdx) => {
                    if (level.modules) {
                        level.modules.forEach((module, modIdx) => {
                            lessons.push({
                                id: module.id,
                                title: module.title,
                                description: module.description,
                                concepts: module.concepts || [],
                                levelNumber: level.levelNumber || levelIdx + 1,
                                levelTitle: level.title,
                                difficulty: level.difficulty || 'beginner',
                                index: lessons.length
                            });
                        });
                    }
                });
            } else if (learningPath.modules) {
                learningPath.modules.forEach((module, idx) => {
                    lessons.push({
                        id: module.id,
                        title: module.title,
                        description: module.description,
                        concepts: module.concepts || [],
                        levelNumber: idx + 1,
                        levelTitle: `Lesson ${idx + 1}`,
                        difficulty: module.difficulty || 'beginner',
                        index: idx
                    });
                });
            }

            setAllLessons(lessons);

            // If no learning path provided but lessonContent exists, don't show picker
            if (lessons.length === 0 && lessonContent) {
                setShowLessonPicker(false);
            }
        } else if (lessonContent) {
            // Direct lesson content provided, skip picker
            setShowLessonPicker(false);
            setCurrentLessonContent(lessonContent);
            setCurrentLessonTitle(lessonTitle || 'Lesson');
        }
    }, [learningPath, lessonContent, lessonTitle]);

    // Silence detection functions
    const startSilenceDetection = useCallback(() => {
        clearSilenceTimer();
        // Don't start if in grace period
        if (gracePeriodRef.current) return;

        silenceTimeoutRef.current = setInterval(() => {
            // Skip if in grace period
            if (gracePeriodRef.current) return;

            const timeSinceLastSpeech = Date.now() - (lastSpeechTimeRef.current || Date.now());
            if (timeSinceLastSpeech >= SILENCE_TIMEOUT && shouldBeListeningRef.current) {
                // 3 seconds of silence after speaking - stop and process
                console.log('⏱️ Silence timeout - stopping and processing');
                stopListeningAndProcess();
            }
        }, 500); // Check every 500ms
    }, []);

    const resetSilenceTimer = useCallback(() => {
        lastSpeechTimeRef.current = Date.now();
    }, []);

    const clearSilenceTimer = useCallback(() => {
        if (silenceTimeoutRef.current) {
            clearInterval(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
        }
    }, []);

    const stopListeningAndProcess = useCallback(() => {
        console.log('🛑 stopListeningAndProcess called');
        clearSilenceTimer();
        shouldBeListeningRef.current = false;
        manualStopRef.current = true;
        try {
            recognitionRef.current?.stop();
        } catch (err) {
            // Ignore
        }
        setIsListening(false);

        // Process the transcript - use ref to get real-time value (avoids stale closure)
        const currentTranscript = transcriptRef.current.trim();
        console.log('📝 Processing transcript:', currentTranscript);
        if (currentTranscript) {
            handleUserMessage(currentTranscript);
        }
        setTranscript('');
        transcriptRef.current = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Play activation sound
    const playActivationSound = useCallback(() => {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = 600;
            oscillator.type = 'sine';
            gainNode.gain.value = 0.15;

            oscillator.start();
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
            oscillator.stop(audioContext.currentTime + 0.3);
        } catch (err) {
            // Audio not supported
        }
    }, []);

    // Start/stop wake word listening
    useEffect(() => {
        if ((wakeWordEnabled || lectureMode) && voiceSupported && !isListening && !isProcessing) {
            try {
                wakeRecognitionRef.current?.start();
            } catch (err) {
                // Already started
            }
        } else if (!wakeWordEnabled && !lectureMode) {
            try {
                wakeRecognitionRef.current?.stop();
            } catch (err) {
                // Already stopped
            }
        }
    }, [wakeWordEnabled, lectureMode, voiceSupported, isListening, isProcessing]);

    // Restart wake listening after speaking ends (not in lecture mode)
    useEffect(() => {
        if (!isSpeaking && !isListening && (wakeWordEnabled || lectureMode) && voiceSupported && !isProcessing) {
            const timeout = setTimeout(() => {
                try {
                    wakeRecognitionRef.current?.start();
                } catch (err) {
                    // Ignore
                }
            }, 500);
            return () => clearTimeout(timeout);
        }
    }, [isSpeaking, isListening, wakeWordEnabled, lectureMode, voiceSupported, isProcessing]);

    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Text-to-speech with professor-like delivery
    const speak = useCallback((text) => {
        return new Promise((resolve) => {
            synthRef.current.cancel();

            const utterance = new SpeechSynthesisUtterance(text);
            utterance.voice = selectedVoice;
            utterance.rate = speechRate * 0.9; // Slightly slower for lecture feel
            utterance.pitch = 0.9; // Slightly lower pitch for authority
            utterance.volume = 1;

            utterance.onstart = () => setIsSpeaking(true);
            utterance.onend = () => {
                setIsSpeaking(false);
                resolve();
            };
            utterance.onerror = () => {
                setIsSpeaking(false);
                resolve();
            };

            synthRef.current.speak(utterance);
        });
    }, [selectedVoice, speechRate]);

    // Handle user message
    const handleUserMessage = async (userMessage) => {
        if (!userMessage.trim()) return;

        setTranscript('');
        setIsProcessing(true);

        const newUserMsg = { role: 'user', content: userMessage };
        setMessages(prev => [...prev, newUserMsg]);
        conversationHistoryRef.current.push(newUserMsg);

        try {
            const response = await fetch(`${API_URL}/api/voice-mentor`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: userMessage,
                    lessonContent: currentLessonContent,
                    conversationHistory: conversationHistoryRef.current.slice(-10),
                    mode: lectureMode ? 'lecture_qa' : 'conversation'
                })
            });

            const data = await response.json();

            if (data.success) {
                const assistantMsg = { role: 'assistant', content: data.response };
                setMessages(prev => [...prev, assistantMsg]);
                conversationHistoryRef.current.push(assistantMsg);

                await speak(data.response);

                // If in lecture mode and paused, prompt to continue
                if (lectureMode && lecturePaused) {
                    const continuePrompt = { role: 'assistant', content: 'Say "please continue" when you\'re ready to resume the lecture.' };
                    setMessages(prev => [...prev, continuePrompt]);
                }
            } else {
                throw new Error(data.error || 'Failed to get response');
            }
        } catch (err) {
            console.error('Error getting mentor response:', err);
            setError('Failed to get response. Please try again.');
        }

        setIsProcessing(false);
    };

    // Start lecture mode - Professor introduction then content
    const startLecture = async (contentOverride = null) => {
        setLectureMode(true);
        setLecturePaused(false);
        setLecturePosition(0);
        setIsProcessing(true);
        setMessages([]);
        setLectureComplete(false);
        conversationHistoryRef.current = [];

        // Use override content if provided, otherwise use state
        const lectureContent = contentOverride || currentLessonContent;
        console.log('📚 Starting lecture with content:', lectureContent?.substring(0, 100));

        // Generate lecture content from lesson
        try {
            const response = await fetch(`${API_URL}/api/voice-mentor`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: '',
                    lessonContent: lectureContent,
                    conversationHistory: [],
                    mode: 'lecture_start'
                })
            });

            const data = await response.json();

            if (data.success && data.lectureChunks) {
                setLectureChunks(data.lectureChunks);

                // Professor introduction
                const introMsg = { role: 'assistant', content: data.introduction };
                setMessages([introMsg]);
                await speak(data.introduction);

                // Start reading first chunk
                setIsProcessing(false);
                readLectureChunk(0, data.lectureChunks);
            } else {
                // Fallback to single lecture
                const introText = "Class is about to start. Please take your seat and be prepared to learn. If you have questions throughout the lecture, just say 'Hey Viva' then ask your question. When you're done with the question, say 'please continue' to resume where we left off. Let's begin.";
                const introMsg = { role: 'assistant', content: introText };
                setMessages([introMsg]);
                await speak(introText);

                // Then read the lecture content
                if (data.response) {
                    const lectureMsg = { role: 'assistant', content: data.response };
                    setMessages(prev => [...prev, lectureMsg]);
                    setLectureChunks([data.response]);
                    setIsProcessing(false);
                    await speak(data.response);
                }
            }
        } catch (err) {
            console.error('Error starting lecture:', err);
            setError('Failed to start lecture.');
            setIsProcessing(false);
        }
    };

    // Read a lecture chunk
    const readLectureChunk = async (index, chunks = lectureChunks) => {
        if (index >= chunks.length || lecturePaused) {
            if (index >= chunks.length) {
                // Lecture complete
                setLectureComplete(true);
                const hasNextLesson = currentLessonIndex < allLessons.length - 1;
                const endMessage = hasNextLesson
                    ? "That concludes today's lecture. Do you have any final questions? Say 'next lesson' when you're ready to continue to the next lesson."
                    : "That concludes today's lecture. Do you have any final questions?";
                const endMsg = { role: 'assistant', content: endMessage };
                setMessages(prev => [...prev, endMsg]);
                await speak(endMessage);
                setLectureMode(false);
            }
            return;
        }

        setLecturePosition(index);
        const chunk = chunks[index];
        const chunkMsg = { role: 'assistant', content: chunk };
        setMessages(prev => [...prev, chunkMsg]);

        await speak(chunk);

        // Continue to next chunk if not paused
        if (!lecturePaused) {
            readLectureChunk(index + 1, chunks);
        }
    };

    // Continue lecture from where we left off
    const continueLecture = async () => {
        const resumeMsg = { role: 'assistant', content: "Alright, let's continue where we left off..." };
        setMessages(prev => [...prev, resumeMsg]);
        await speak("Alright, let's continue where we left off...");
        readLectureChunk(lecturePosition, lectureChunks);
    };

    // Select a lesson from the picker
    const selectLesson = async (lesson, index) => {
        setSelectedLesson(lesson);
        setCurrentLessonIndex(index);

        // Build lesson content from the lesson data
        let content = `LESSON: ${lesson.title}\n`;
        content += `Level: ${lesson.levelNumber} - ${lesson.levelTitle}\n`;
        content += `Difficulty: ${lesson.difficulty}\n\n`;
        content += `DESCRIPTION: ${lesson.description}\n\n`;
        if (lesson.concepts && lesson.concepts.length > 0) {
            content += `KEY CONCEPTS: ${lesson.concepts.join(', ')}\n\n`;
        }
        if (lesson.realWorldApplication) {
            content += `REAL WORLD APPLICATION: ${lesson.realWorldApplication}\n\n`;
        }

        setCurrentLessonContent(content);
        setCurrentLessonTitle(lesson.title);
        setShowLessonPicker(false);

        // Pass content directly to avoid state timing issues
        startLecture(content);
    };

    // Go to next lesson
    const goToNextLesson = useCallback(async () => {
        if (currentLessonIndex < allLessons.length - 1) {
            const nextIndex = currentLessonIndex + 1;
            const nextLesson = allLessons[nextIndex];

            const transitionMsg = { role: 'assistant', content: `Moving to the next lesson: ${nextLesson.title}` };
            setMessages(prev => [...prev, transitionMsg]);
            await speak(`Moving to the next lesson: ${nextLesson.title}`);

            selectLesson(nextLesson, nextIndex);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLessonIndex, allLessons, speak]);

    // Keep ref updated
    useEffect(() => {
        goToNextLessonRef.current = goToNextLesson;
    }, [goToNextLesson]);

    // Toggle listening with proper continuous mode
    const toggleListening = () => {
        console.log('🔘 toggleListening called - isListening:', isListening, 'shouldBe:', shouldBeListeningRef.current);

        if (isListening || shouldBeListeningRef.current) {
            // Stop listening and process what we have
            stopListeningAndProcess();
        } else if (!isSpeaking && !isProcessing) {
            setTranscript('');
            transcriptRef.current = '';

            // If in lecture mode, pause the lecture
            if (lectureMode && isSpeaking) {
                synthRef.current.cancel();
                setIsSpeaking(false);
                setLecturePaused(true);
            }

            // Stop wake recognition first to avoid conflicts
            try {
                wakeRecognitionRef.current?.stop();
            } catch (err) {
                // Ignore
            }

            // Start main recognition
            try {
                shouldBeListeningRef.current = true;
                manualStopRef.current = false;
                recognitionRef.current?.start();
                console.log('🎤 Recognition START requested');
            } catch (err) {
                console.error('Error starting recognition:', err);
                shouldBeListeningRef.current = false;
            }
        }
    };

    // Stop speaking
    const stopSpeaking = () => {
        synthRef.current.cancel();
        setIsSpeaking(false);
        if (lectureMode) {
            setLecturePaused(true);
        }
    };

    // Get status message
    const getStatusMessage = () => {
        if (isListening) {
            if (transcript) {
                return 'Listening... (stops 3s after you finish talking)';
            }
            return 'Listening... Start speaking!';
        }
        if (isProcessing) return 'Thinking...';
        if (isSpeaking) return lectureMode ? 'Lecturing... Say "Hey Viva" to ask a question' : 'Speaking...';
        if (lecturePaused) return 'Lecture paused - Say "Please continue" to resume';
        if (isWakeListening && wakeWordEnabled) return 'Say "Hey Viva" or tap the mic';
        return 'Tap the mic to ask a question';
    };

    return (
        <div className="voice-mentor-overlay">
            <div className="voice-mentor-container">
                {/* Header */}
                <div className="mentor-header">
                    <div className="mentor-title">
                        <span className="mentor-icon">🧙‍♂️</span>
                        <div>
                            <h2>Professor Viva</h2>
                            <p className="mentor-subtitle">{currentLessonTitle || learningPath?.topic || 'Voice Tutoring Session'}</p>
                        </div>
                    </div>
                    <div className="mentor-actions">
                        <button
                            className="settings-btn"
                            onClick={() => setShowSettings(!showSettings)}
                        >
                            ⚙️
                        </button>
                        <button className="close-mentor-btn" onClick={onClose}>✕</button>
                    </div>
                </div>

                {/* Lesson Picker */}
                {showLessonPicker && allLessons.length > 0 && (
                    <div className="lesson-picker">
                        <div className="lesson-picker-header">
                            <h3>📚 Select a Lesson</h3>
                            <p>Choose which lesson you'd like Professor Viva to teach</p>
                        </div>
                        <div className="lesson-picker-list">
                            {allLessons.map((lesson, idx) => (
                                <button
                                    key={lesson.id || idx}
                                    className="lesson-picker-item"
                                    onClick={() => selectLesson(lesson, idx)}
                                >
                                    <div className="lesson-picker-level">
                                        Level {lesson.levelNumber}
                                    </div>
                                    <div className="lesson-picker-content">
                                        <div className="lesson-picker-title">{lesson.title}</div>
                                        <div className="lesson-picker-description">{lesson.description}</div>
                                        {lesson.concepts && lesson.concepts.length > 0 && (
                                            <div className="lesson-picker-concepts">
                                                {lesson.concepts.slice(0, 3).join(' • ')}
                                                {lesson.concepts.length > 3 && ` +${lesson.concepts.length - 3} more`}
                                            </div>
                                        )}
                                    </div>
                                    <div className="lesson-picker-arrow">→</div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Settings Panel */}
                {showSettings && (
                    <div className="mentor-settings">
                        <div className="setting-item">
                            <label>Voice</label>
                            <select
                                className="voice-select"
                                value={selectedVoice?.name || ''}
                                onChange={(e) => {
                                    const voice = availableVoices.find(v => v.name === e.target.value);
                                    if (voice) setSelectedVoice(voice);
                                }}
                            >
                                {availableVoices.map((voice, idx) => (
                                    <option key={idx} value={voice.name}>
                                        {voice.name.replace('Microsoft ', '').replace(' Online', '')}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="setting-item">
                            <label>Speech Speed</label>
                            <input
                                type="range"
                                min="0.5"
                                max="1.5"
                                step="0.1"
                                value={speechRate}
                                onChange={(e) => setSpeechRate(parseFloat(e.target.value))}
                            />
                            <span>{speechRate}x</span>
                        </div>
                        <div className="setting-item">
                            <label>Wake Word</label>
                            <button
                                className={`wake-toggle ${wakeWordEnabled ? 'active' : ''}`}
                                onClick={() => setWakeWordEnabled(!wakeWordEnabled)}
                            >
                                {wakeWordEnabled ? '✓ "Hey Viva" Enabled' : 'Disabled'}
                            </button>
                        </div>
                        {wakeWordEnabled && (
                            <div className="wake-word-hint">
                                Say "Hey Viva" to interrupt and ask questions
                            </div>
                        )}
                    </div>
                )}

                {/* Mode Selector - Start Lecture Button */}
                {!showLessonPicker && (
                    <div className="mode-selector">
                        {!lectureMode && !lectureComplete && (
                            <button
                                className="mode-btn start-lecture-btn"
                                onClick={startLecture}
                                disabled={isProcessing}
                            >
                                🎓 Start Class
                            </button>
                        )}
                        {lectureMode && (
                            <div className="lecture-progress-indicator">
                                🎓 Lecture in Progress
                            </div>
                        )}
                        {lectureMode && lecturePaused && (
                            <button
                                className="mode-btn resume-btn"
                                onClick={continueLecture}
                            >
                                ▶️ Resume Lecture
                            </button>
                        )}
                        {lectureComplete && currentLessonIndex < allLessons.length - 1 && (
                            <button
                                className="mode-btn next-lesson-btn"
                                onClick={goToNextLesson}
                                disabled={isProcessing}
                            >
                                ➡️ Continue to Next Lesson
                            </button>
                        )}
                        {lectureComplete && allLessons.length > 0 && (
                            <button
                                className="mode-btn pick-lesson-btn"
                                onClick={() => {
                                    setShowLessonPicker(true);
                                    setLectureComplete(false);
                                    setMessages([]);
                                }}
                            >
                                📚 Pick Another Lesson
                            </button>
                        )}
                    </div>
                )}

                {/* Error Display */}
                {error && (
                    <div className="mentor-error">
                        <span>⚠️ {error}</span>
                        <button onClick={() => setError(null)}>Dismiss</button>
                    </div>
                )}

                {/* Messages Area - Hidden during lesson picker */}
                {!showLessonPicker && (
                    <>
                        <div className="mentor-messages">
                            {messages.length === 0 ? (
                                <div className="mentor-welcome">
                                    <div className="welcome-icon">🧙‍♂️</div>
                                    <h3>Welcome to Professor Viva's Class!</h3>
                                    <p>
                                        {currentLessonTitle ? `Ready to learn: ${currentLessonTitle}` : 'Select a lesson to begin.'}
                                    </p>
                                    <div className="welcome-tips">
                                        <div className="tip lecture-tip">🎓 Press "Start Class" to begin</div>
                                        <div className="tip wake-tip">🗣️ Say "Hey Viva" to ask questions</div>
                                        <div className="tip">💡 "Please continue" resumes lecture</div>
                                        <div className="tip">➡️ "Next lesson" when lecture ends</div>
                                        <div className="tip close-tip">👋 Say "Goodbye Viva" to close</div>
                                    </div>
                                </div>
                            ) : (
                                messages.map((msg, idx) => (
                                    <div key={idx} className={`message ${msg.role}`}>
                                        <div className="message-avatar">
                                            {msg.role === 'user' ? '👤' : '🧙‍♂️'}
                                        </div>
                                        <div className="message-content">
                                            {msg.content}
                                        </div>
                                    </div>
                                ))
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Current Transcript */}
                        {transcript && (
                            <div className="current-transcript">
                                <span className="transcript-label">You:</span>
                                <span className="transcript-text">{transcript}</span>
                            </div>
                        )}

                        {/* Status Bar */}
                        <div className="mentor-status">
                            <div className={`status-indicator ${isListening ? 'listening' : isSpeaking ? 'speaking' : isProcessing ? 'processing' : lecturePaused ? 'paused' : ''}`}>
                                {isListening && <div className="pulse-ring" />}
                                <span>{getStatusMessage()}</span>
                            </div>
                        </div>

                        {/* Controls */}
                        <div className="mentor-controls">
                            {isSpeaking ? (
                                <button className="control-btn stop-btn" onClick={stopSpeaking}>
                                    <span className="btn-icon">⏸️</span>
                                    <span className="btn-label">Pause</span>
                                </button>
                            ) : (
                                <button
                                    className={`control-btn mic-btn ${isListening ? 'active' : ''}`}
                                    onClick={toggleListening}
                                    disabled={!voiceSupported || isProcessing}
                                >
                                    <span className="btn-icon">{isListening ? '🔴' : '🎙️'}</span>
                                    <span className="btn-label">{isListening ? 'Listening...' : 'Ask Question'}</span>
                                </button>
                            )}
                        </div>

                        {/* Keyboard Input Fallback */}
                        <div className="keyboard-fallback">
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                const input = e.target.elements.textInput;
                                if (input.value.trim()) {
                                    if (lectureMode && isSpeaking) {
                                        synthRef.current.cancel();
                                        setIsSpeaking(false);
                                        setLecturePaused(true);
                                    }
                                    handleUserMessage(input.value.trim());
                                    input.value = '';
                                }
                            }}>
                                <input
                                    type="text"
                                    name="textInput"
                                    placeholder="Or type your question here..."
                                    disabled={isProcessing}
                                />
                                <button type="submit" disabled={isProcessing}>
                                    Send
                                </button>
                            </form>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default VoiceMentor;
